import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import * as api from 'util/api.js';
import usePricingDiscrepancyOverviewQuery, {
  invalidatePricingDiscrepancyQueries,
} from './use-pricing-discrepancy-scan-query';
import BeerTable from './BeerTable';
import BeerDrawer from './BeerDrawer';
import PlatformStatusGrid from './PlatformStatusGrid';
import PriceChangeLedger from './PriceChangeLedger';
import {
  errorMessage,
  missingPlatformLabels,
  skuHasMissingPlatformLink,
  skuIssueCounts,
  skuSystemCounts,
} from './helpers';

const { Text } = Typography;

const SYSTEM_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'monday', label: 'Monday' },
  { value: 'cin7', label: 'Cin7' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'kounta', label: 'Kounta' },
];

const CONTAINER_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'can', label: 'Can' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'keg', label: 'Keg' },
];

function kountaFieldLabel(field) {
  if (field === 'sell_price_inc_tax') return 'sell';
  if (field === 'cost_price_inc_tax') return 'cost';
  return field || 'price';
}

function formatQueueValue(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

function kountaApplyConfirmContent(changes, newerCount) {
  return (
    <div>
      <p>
        {changes.length}
        {' '}
        queued change(s) will be marked applied.
      </p>
      <div style={{
        maxHeight: 240,
        overflow: 'auto',
      }}
      >
        <ul style={{
          paddingLeft: 18,
          margin: 0,
        }}
        >
          {changes.map((change) => (
            <li key={change.id}>
              {change.skuName || `SKU ${change.skuKey}`}
              {' · '}
              {change.productName || 'Kounta'}
              {' · '}
              {kountaFieldLabel(change.field)}
              {': '}
              {formatQueueValue(change.previousValue)}
              {' → '}
              {formatQueueValue(change.newValue)}
            </li>
          ))}
        </ul>
      </div>
      {newerCount > 0 ? (
        <p>
          {newerCount}
          {' '}
          newer change(s) will stay pending.
        </p>
      ) : null}
    </div>
  );
}

function beerKey(beer) {
  return beer?.productMatrixId == null ? '__unassigned__' : String(beer.productMatrixId);
}

function skuMatchesSearch(sku, q) {
  return (sku.skuName || '').toLowerCase().includes(q)
    || String(sku.skuKey).includes(q)
    || (sku.cin7Sku || '').toLowerCase().includes(q);
}

export default function PricingDiscrepancies() {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [containerFilter, setContainerFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');
  const [onlyDiscrepancies, setOnlyDiscrepancies] = useState(true);
  const [showMissing, setShowMissing] = useState(false);
  const [showMisalignment, setShowMisalignment] = useState(false);
  const [highlightMissingLinks, setHighlightMissingLinks] = useState(false);
  const [missingLinksMode, setMissingLinksMode] = useState(false);
  const [includeLegacy, setIncludeLegacy] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerSkuKey, setLedgerSkuKey] = useState(null);
  const [ledgerRefreshKey, setLedgerRefreshKey] = useState(0);
  const [kountaDownloadUpTo, setKountaDownloadUpTo] = useState(null);
  const [kountaDownloading, setKountaDownloading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBeerKey, setSelectedBeerKey] = useState(null);
  const {
    data: overview,
    isLoading,
    isFetching,
    error,
    dataUpdatedAt,
  } = usePricingDiscrepancyOverviewQuery(includeLegacy);

  const onDownloadAccumulatedKounta = useCallback(async () => {
    setKountaDownloading(true);
    try {
      const queue = await api.getKountaQueue();
      const upTo = queue?.maxChangeId;
      if (!upTo) {
        setKountaDownloadUpTo(null);
        notification.warning({ message: 'No pending Kounta rows' });
        return;
      }
      await api.downloadAccumulatedKountaCsv(upTo);
      setKountaDownloadUpTo(upTo);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Download failed') });
    } finally {
      setKountaDownloading(false);
    }
  }, []);

  const onMarkKountaApplied = useCallback(async () => {
    const upTo = kountaDownloadUpTo;
    if (upTo == null) return;
    let queue;
    try {
      queue = await api.getKountaQueue();
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Could not load Kounta queue') });
      return;
    }
    const changes = queue?.changes || [];
    const included = changes.filter((change) => change.id <= upTo);
    const newerCount = changes.filter((change) => change.id > upTo).length;
    if (included.length === 0) {
      setKountaDownloadUpTo(null);
      invalidatePricingDiscrepancyQueries(queryClient);
      notification.info({ message: 'Those rows were already marked applied' });
      return;
    }
    Modal.confirm({
      title: 'Mark Kounta export applied?',
      width: 640,
      content: kountaApplyConfirmContent(included, newerCount),
      okText: 'Mark applied',
      onOk: () => api.applyKountaQueue(upTo).then(() => {
        setKountaDownloadUpTo(null);
        invalidatePricingDiscrepancyQueries(queryClient);
        setLedgerRefreshKey((n) => n + 1);
        notification.success({ message: 'Kounta export marked applied' });
      }).catch((err) => {
        const message = errorMessage(err, 'Could not mark export applied');
        if (/already applied/i.test(message)) {
          setKountaDownloadUpTo(null);
          invalidatePricingDiscrepancyQueries(queryClient);
          notification.warning({ message: 'Already marked applied' });
          return;
        }
        notification.error({ message });
        throw err;
      }),
    });
  }, [kountaDownloadUpTo, queryClient]);

  const allBeers = useMemo(() => overview?.beers || [], [overview]);

  const filteredBeers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const matchesSku = (sku) => skuMatchesSearch(sku, q);
    const matchesBeer = (beer, skus) => {
      if (!q) return true;
      const nameHit = (beer.productName || '').toLowerCase().includes(q);
      return nameHit || (skus || []).some(matchesSku);
    };
    const byContainer = (skus) => (
      containerFilter === 'all'
        ? skus
        : skus.filter((s) => String(s.containerType || '').toLowerCase() === containerFilter)
    );
    if (missingLinksMode) {
      return allBeers
        .map((beer) => {
          const containerSkus = byContainer(beer.skus || []);
          let skus = containerSkus.filter(
            (s) => skuHasMissingPlatformLink(s, systemFilter),
          );
          if (q && skus.length === 0 && matchesBeer(beer, containerSkus)) {
            skus = containerSkus.filter((s) => (
              (beer.productName || '').toLowerCase().includes(q) || matchesSku(s)
            ));
          }
          return { ...beer, skus };
        })
        .filter((beer) => (beer.skus || []).length > 0 && matchesBeer(beer, beer.skus))
        .sort((a, b) => {
          const gap = (beer) => (beer.skus || []).reduce(
            (n, sku) => n + missingPlatformLabels(sku).length,
            0,
          );
          const byGap = gap(b) - gap(a);
          if (byGap !== 0) return byGap;
          return String(a.productName || '').localeCompare(String(b.productName || ''));
        });
    }
    const skuShown = (sku) => {
      const issues = skuIssueCounts(sku);
      const clean = issues.discrepancy === 0 && issues.missing === 0 && issues.misalignment === 0;
      if (issues.discrepancy > 0) return true;
      if (skuSystemCounts(sku).kounta > 0) return true;
      if (showMissing && issues.missing > 0) return true;
      if (showMisalignment && issues.misalignment > 0) return true;
      return !onlyDiscrepancies && clean;
    };
    return allBeers
      .map((beer) => {
        const containerSkus = byContainer(beer.skus || []);
        let skus = containerSkus.filter(skuShown);
        if (systemFilter !== 'all') {
          skus = skus.filter((s) => skuSystemCounts(s)[systemFilter] > 0);
        }
        if (q && skus.length === 0 && matchesBeer(beer, containerSkus)) {
          const nameHit = (beer.productName || '').toLowerCase().includes(q);
          skus = containerSkus.filter((s) => nameHit || matchesSku(s));
        }
        return { ...beer, skus };
      })
      .filter((beer) => (beer.skus || []).length > 0 && matchesBeer(beer, beer.skus))
      .sort((a, b) => (b.counts?.total || 0) - (a.counts?.total || 0));
  }, [
    allBeers,
    searchText,
    containerFilter,
    onlyDiscrepancies,
    showMissing,
    showMisalignment,
    systemFilter,
    missingLinksMode,
  ]);

  const selectedBeer = useMemo(() => {
    if (selectedBeerKey == null) return null;
    return allBeers.find((b) => beerKey(b) === selectedBeerKey) || null;
  }, [allBeers, selectedBeerKey]);

  const openDrawer = useCallback((beer) => {
    setSelectedBeerKey(beerKey(beer));
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedBeerKey(null);
  }, []);

  const summary = overview?.summary;
  const accumulatedCount = overview?.accumulatedKountaCsvRowCount ?? 0;
  const statsLoading = isLoading && !summary;
  const sourceFreshness = overview?.sourceFreshness;
  const discrepancySkus = allBeers.reduce((n, beer) => (
    n + (beer.skus || []).filter((s) => skuIssueCounts(s).discrepancy > 0).length
  ), 0);

  const countValue = (value) => {
    if (statsLoading) return '…';
    if (value == null) return '—';
    return value;
  };
  const platformCounts = [
    { label: 'Monday', value: summary?.skuWithMondayDiscrepancies },
    { label: 'Cin7', value: summary?.skuWithCin7Discrepancies },
    { label: 'Shopify', value: summary?.skuWithShopifyDiscrepancies },
    { label: 'Kounta', value: summary?.skuWithKountaDiscrepancies },
    ...(showMissing ? [{ label: 'Missing prices', value: summary?.skuWithMissing }] : []),
    ...(showMisalignment ? [{ label: 'Misalignment', value: summary?.skuWithMisalignment }] : []),
  ];

  return (
    <div className="pricing-discrepancies-page" style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <PlatformStatusGrid sourceFreshness={sourceFreshness} disabled={isFetching} />

        {error ? (
          <Alert
            type="error"
            showIcon
            message={errorMessage(error, 'Failed to compute discrepancies')}
          />
        ) : null}

        <Card
          size="small"
          title="Comparison"
          extra={(
            <Space>
              <Button
                onClick={() => { setLedgerSkuKey(null); setLedgerOpen(true); }}
              >
                Price change ledger
              </Button>
              <Button
                type="primary"
                onClick={() => invalidatePricingDiscrepancyQueries(queryClient)}
                loading={isFetching}
              >
                Recompute
              </Button>
            </Space>
          )}
        >
          <Row gutter={[24, 16]} align="top">
            <Col xs={24} lg={15}>
              <Space size={28} wrap align="start">
                <Statistic
                  title="SKUs that disagree"
                  value={countValue(summary ? discrepancySkus : null)}
                />
                <Statistic title="Scanned" value={countValue(summary?.totalSkus)} />
                {platformCounts.map((item) => (
                  <Statistic
                    key={item.label}
                    title={item.label}
                    value={countValue(item.value)}
                    valueStyle={{ fontSize: 20 }}
                  />
                ))}
              </Space>
              {dataUpdatedAt ? (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">
                    Computed
                    {' '}
                    {new Date(dataUpdatedAt).toLocaleString()}
                  </Text>
                </div>
              ) : null}
            </Col>
            <Col xs={24} lg={9}>
              <Text strong>Kounta export</Text>
              <div style={{ margin: '4px 0 8px' }}>
                <Text type="secondary">
                  {accumulatedCount}
                  {' '}
                  pending price change
                  {accumulatedCount === 1 ? '' : 's'}
                  {' '}
                  to upload.
                </Text>
              </div>
              <Space wrap>
                <Button
                  onClick={onDownloadAccumulatedKounta}
                  disabled={accumulatedCount === 0}
                  loading={kountaDownloading}
                >
                  Download CSV
                </Button>
                <Tooltip title={kountaDownloadUpTo == null ? 'Download CSV first' : null}>
                  <span>
                    <Button
                      disabled={kountaDownloadUpTo == null}
                      onClick={onMarkKountaApplied}
                    >
                      Mark applied
                    </Button>
                  </span>
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Find">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Radio.Group
              value={missingLinksMode ? 'links' : 'prices'}
              onChange={(e) => setMissingLinksMode(e.target.value === 'links')}
            >
              <Radio.Button value="prices">Discrepancies</Radio.Button>
              <Radio.Button value="links">Missing links</Radio.Button>
            </Radio.Group>
            <Space wrap size="middle" align="center">
              <Input.Search
                placeholder="Search beer, SKU, Matrix ID, Cin7 SKU"
                allowClear
                style={{ width: 320 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Space size={8}>
                <Text type="secondary">Container</Text>
                <Select
                  style={{ width: 120 }}
                  value={containerFilter}
                  onChange={setContainerFilter}
                  options={CONTAINER_FILTER_OPTIONS}
                />
              </Space>
              <Space size={8}>
                <Text type="secondary">{missingLinksMode ? 'Missing on' : 'System'}</Text>
                <Select
                  style={{ width: 140 }}
                  value={systemFilter}
                  onChange={setSystemFilter}
                  options={SYSTEM_FILTER_OPTIONS}
                />
              </Space>
            </Space>
            <Space wrap size="middle" align="center">
              {missingLinksMode ? null : (
                <Space>
                  <Switch checked={onlyDiscrepancies} onChange={setOnlyDiscrepancies} />
                  <Text>Hide clean SKUs</Text>
                </Space>
              )}
              {missingLinksMode ? null : (
                <Space>
                  <Switch checked={showMissing} onChange={setShowMissing} />
                  <Text>Missing prices</Text>
                </Space>
              )}
              {missingLinksMode ? null : (
                <Space>
                  <Switch checked={showMisalignment} onChange={setShowMisalignment} />
                  <Text>Misalignment</Text>
                </Space>
              )}
              {missingLinksMode ? null : (
                <Space>
                  <Switch checked={highlightMissingLinks} onChange={setHighlightMissingLinks} />
                  <Text>Highlight missing links</Text>
                </Space>
              )}
              <Space>
                <Switch checked={includeLegacy} onChange={setIncludeLegacy} />
                <Text>Legacy SKUs</Text>
              </Space>
              <Text type="secondary">
                {filteredBeers.length}
                {' '}
                of
                {' '}
                {allBeers.length}
                {' '}
                beers
              </Text>
            </Space>
          </Space>
        </Card>

        <Card title={missingLinksMode ? 'Missing links' : 'Results'}>
          <BeerTable
            beers={filteredBeers}
            loading={isLoading}
            onOpen={openDrawer}
            showMissing={showMissing}
            showMisalignment={showMisalignment}
            missingLinksMode={missingLinksMode}
          />
        </Card>
      </Space>

      <BeerDrawer
        beerKey={selectedBeerKey}
        beerSummary={selectedBeer}
        open={drawerOpen}
        onClose={closeDrawer}
        includeLegacy={includeLegacy}
        highlightMissingLinks={highlightMissingLinks || missingLinksMode}
        onOpenLedger={(skuKey) => { setLedgerSkuKey(skuKey); setLedgerOpen(true); }}
      />
      <PriceChangeLedger
        open={ledgerOpen}
        skuKey={ledgerSkuKey}
        refreshKey={ledgerRefreshKey}
        onClose={() => { setLedgerOpen(false); setLedgerSkuKey(null); }}
      />
    </div>
  );
}
